import { TextboardTextStyles } from './textboard-content';

/**
 * Bridge the theme's CSS custom properties into concrete fabric text styles (design D5): a
 * canvas cannot resolve `var(...)`, so the values are read via `getComputedStyle` on an element
 * inside the app's DOM (the properties are set on `body` by the `svg-canvas-theme` mixin and
 * inherit — the renderer's canvas element works). Called from the textboard capability factory,
 * so the values are re-resolved per capability creation; theme changes need no live tracking —
 * brands are separate builds with no runtime theme switch.
 */
export function resolveTextboardStyles(element: HTMLElement): TextboardTextStyles {
    const style = getComputedStyle(element);
    const value = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
    // A CSS var authored across multiple SCSS lines is returned with embedded newlines. A canvas
    // 2D context SILENTLY rejects a `font` string containing a newline (it keeps the previous
    // 10px sans-serif default → wrong metrics → wrong wrap), and `document.fonts.load()` throws
    // on it. Collapse all whitespace so the font-family list is a valid one-line CSS value.
    const fontValue = (name: string, fallback: string) =>
        style.getPropertyValue(name).replace(/\s+/g, ' ').trim() || fallback;
    const defaultFont = "'Roboto Variable', Roboto, 'Helvetica Neue', sans-serif";
    // `bullet-color` is `inherit` when the brand theme doesn't override it (TUT) — fabric can't
    // use `inherit`, so map it (and an unset var) to null so the capability falls back to the text color.
    const rawBullet = value('--slide-sync-svg-canvas-textboard-bullet-color', 'inherit');
    // The SVG textboard's `<text>` inherits the app's `body { letter-spacing }` and wraps with it;
    // fabric ignores CSS, so bridge the host's computed value and replay it as `charSpacing`.
    // `normal` (or unparseable) → 0.
    const letterSpacingPx = Number.parseFloat(style.letterSpacing) || 0;
    return {
        color: value('--slide-sync-svg-canvas-textboard-color', 'black'),
        lightColor: value('--slide-sync-svg-canvas-textboard-light-color', 'white'),
        bulletColor: rawBullet === 'inherit' ? null : rawBullet,
        fontFamily: fontValue('--slide-sync-svg-canvas-textboard-font-family', defaultFont),
        headlineColor: value('--slide-sync-svg-canvas-headline-color', 'black'),
        headlineFontFamily: fontValue('--slide-sync-svg-canvas-headline-font-family', defaultFont),
        letterSpacingPx,
    };
}
